import { useState, useEffect, useCallback, useMemo } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

// ─── Icon helper ─────────────────────────────────────────
function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

// ─── Types ───────────────────────────────────────────────
interface ClienteInfo {
  id: string;
  razon_social: string;
  nombre_comercial: string | null;
}

interface DatoFinanciero {
  id: string;
  cliente_id: string;
  periodo: string;
  ingresos: number;
  gastos: number;
  resultado: number;
  margen: number;
  riesgo_ia: number | null;
}

interface TaxSummary {
  id: string;
  cliente_id: string;
  periodo: string;
  iva_repercutido: number;
  iva_soportado: number;
  iva_resultado: number;
  base_imponible_emitidas: number;
  base_imponible_recibidas: number;
  retenciones_practicadas: number;
  retenciones_soportadas: number;
  total_suplidos: number;
  recargo_equivalencia: number;
  num_facturas_emitidas: number;
  num_facturas_recibidas: number;
  last_sync_at: string | null;
  ejercicio_fiscal: string;
}

interface DocCount {
  pendientes: number;
  contabilizados: number;
  incidencias: number;
  total: number;
}

type PeriodFilter = 'mes' | 'trimestre' | 'year2025' | 'year2026';

// ─── Constants ───────────────────────────────────────────
const SHORT_MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: 'mes', label: 'Mes actual' },
  { value: 'trimestre', label: 'Trimestre' },
  { value: 'year2025', label: 'Año 2025' },
  { value: 'year2026', label: 'Año 2026' },
];

// ─── Helpers ─────────────────────────────────────────────
const num = (v: unknown): number => parseFloat(String(v ?? 0)) || 0;

function formatEur(value: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
}

function getCurrentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getThreeMonthsAgo(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'ahora mismo';
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHrs < 24) return `hace ${diffHrs}h`;
  if (diffDays < 7) return `hace ${diffDays}d`;
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

// ─── Main Component ──────────────────────────────────────
export default function ClientePanelFinanciero() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();

  const [clienteInfo, setClienteInfo] = useState<ClienteInfo | null>(null);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('mes');
  const [datosFinancieros, setDatosFinancieros] = useState<DatoFinanciero[]>([]);
  const [taxSummaries, setTaxSummaries] = useState<TaxSummary[]>([]);
  const [docCounts, setDocCounts] = useState<DocCount>({ pendientes: 0, contabilizados: 0, incidencias: 0, total: 0 });
  const [loadingCliente, setLoadingCliente] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  // ── Load cliente info ────────────────────────────────────
  const loadCliente = useCallback(async () => {
    if (!user?.id) return;
    setLoadingCliente(true);
    try {
      const { data, error } = await supabase
        .from('clientes')
        .select('id, razon_social, nombre_comercial')
        .eq('user_id', user.id)
        .single();

      if (error || !data) {
        console.error('[PanelFinanciero] No se encontró cliente:', error);
        setClienteInfo(null);
      } else {
        setClienteInfo(data as ClienteInfo);
      }
    } catch (err) {
      console.error('[PanelFinanciero] Error cargando cliente:', err);
    } finally {
      setLoadingCliente(false);
    }
  }, [user?.id]);

  // ── Build Supabase query filters based on period ─────────
  const buildPeriodQuery = useCallback((query: any, filter: PeriodFilter) => {
    switch (filter) {
      case 'mes':
        return query.eq('periodo', getCurrentPeriod());
      case 'trimestre':
        return query.gte('periodo', getThreeMonthsAgo()).lte('periodo', getCurrentPeriod());
      case 'year2025':
        return query.like('periodo', '2025-%');
      case 'year2026':
        return query.like('periodo', '2026-%');
    }
  }, []);

  // ── Load dashboard data ──────────────────────────────────
  const loadData = useCallback(async (cId: string, filter: PeriodFilter) => {
    setLoadingData(true);
    try {
      // Build filtered queries
      let datosQuery = supabase.from('datos_financieros').select('*').eq('cliente_id', cId);
      datosQuery = buildPeriodQuery(datosQuery, filter);

      let taxQuery = supabase.from('tax_summary').select('*').eq('cliente_id', cId);
      taxQuery = buildPeriodQuery(taxQuery, filter);

      const docsQuery = supabase.from('documentos').select('id, estado').eq('cliente_id', cId);

      // Parallel fetch
      const [datosRes, taxRes, docsRes] = await Promise.all([
        datosQuery,
        taxQuery,
        docsQuery,
      ]);

      // datos_financieros
      const datos = (datosRes.data || []) as DatoFinanciero[];
      setDatosFinancieros(datos);

      // tax_summary
      const taxes = (taxRes.data || []) as TaxSummary[];
      setTaxSummaries(taxes);

      // Last sync timestamp
      const latestSync = taxes
        .filter(t => t.last_sync_at)
        .sort((a, b) => (b.last_sync_at || '').localeCompare(a.last_sync_at || ''))
        [0]?.last_sync_at || null;
      setLastSyncAt(latestSync);

      // documentos counts
      const docs = (docsRes.data || []) as { id: string; estado: string }[];
      const counts: DocCount = { pendientes: 0, contabilizados: 0, incidencias: 0, total: docs.length };
      docs.forEach(d => {
        if (d.estado === 'pendiente' || d.estado === 'procesado_ia') counts.pendientes++;
        else if (d.estado === 'contabilizado') counts.contabilizados++;
        else if (d.estado === 'incidencia') counts.incidencias++;
      });
      setDocCounts(counts);
    } catch (err) {
      console.error('[PanelFinanciero] Error cargando datos:', err);
    } finally {
      setLoadingData(false);
    }
  }, [buildPeriodQuery]);

  // ── Effects ──────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && (!user || user.rol !== 'cliente')) {
      router.push('/');
    }
    if (user?.rol === 'cliente') {
      loadCliente();
    }
  }, [user, authLoading, router, loadCliente]);

  useEffect(() => {
    if (clienteInfo?.id) {
      loadData(clienteInfo.id, periodFilter);
    }
  }, [clienteInfo?.id, periodFilter, loadData]);

  // ── Computed: KPI totals ─────────────────────────────────
  const kpis = useMemo(() => {
    const totalIngresos = datosFinancieros.reduce((s, d) => s + num(d.ingresos), 0);
    const totalGastos = datosFinancieros.reduce((s, d) => s + num(d.gastos), 0);
    const totalResultado = datosFinancieros.reduce((s, d) => s + num(d.resultado), 0);
    const margen = totalIngresos > 0 ? (totalResultado / totalIngresos) * 100 : 0;
    return { totalIngresos, totalGastos, totalResultado, margen };
  }, [datosFinancieros]);

  // ── Computed: Tax totals ─────────────────────────────────
  const taxTotals = useMemo(() => {
    return taxSummaries.reduce(
      (acc, t) => ({
        ivaRepercutido: acc.ivaRepercutido + num(t.iva_repercutido),
        ivaSoportado: acc.ivaSoportado + num(t.iva_soportado),
        ivaResultado: acc.ivaResultado + num(t.iva_resultado),
        retencionesPracticadas: acc.retencionesPracticadas + num(t.retenciones_practicadas),
        retencionesSoportadas: acc.retencionesSoportadas + num(t.retenciones_soportadas),
        totalSuplidos: acc.totalSuplidos + num(t.total_suplidos),
        baseImponibleEmitidas: acc.baseImponibleEmitidas + num(t.base_imponible_emitidas),
        baseImponibleRecibidas: acc.baseImponibleRecibidas + num(t.base_imponible_recibidas),
      }),
      {
        ivaRepercutido: 0,
        ivaSoportado: 0,
        ivaResultado: 0,
        retencionesPracticadas: 0,
        retencionesSoportadas: 0,
        totalSuplidos: 0,
        baseImponibleEmitidas: 0,
        baseImponibleRecibidas: 0,
      }
    );
  }, [taxSummaries]);

  // ── Computed: latest invoicing summary ───────────────────
  const latestInvoicing = useMemo(() => {
    if (taxSummaries.length === 0) return null;
    const sorted = [...taxSummaries].sort((a, b) => b.periodo.localeCompare(a.periodo));
    return sorted[0];
  }, [taxSummaries]);

  // ── Computed: monthly chart data (from datos_financieros) ─
  const monthlyChartData = useMemo(() => {
    const sorted = [...datosFinancieros].sort((a, b) => a.periodo.localeCompare(b.periodo));
    return sorted.map(d => ({
      periodo: d.periodo,
      ingresos: num(d.ingresos),
      gastos: num(d.gastos),
    }));
  }, [datosFinancieros]);

  // ── Loading skeleton ─────────────────────────────────────
  if (authLoading || loadingCliente) {
    return (
      <Layout rol="cliente">
        <Head><title>Panel Financiero — Externaliza2</title></Head>
        <div className="px-8 md:px-12 pb-12 space-y-10 pt-8">
          <Skeleton className="h-14 w-80 rounded-huge" />
          <Skeleton className="h-5 w-96 rounded-huge" />
          <div className="flex gap-2">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-28 rounded-2xl" />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-36 rounded-huge" />
            ))}
          </div>
          <Skeleton className="h-48 rounded-huge" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-huge" />
            ))}
          </div>
          <Skeleton className="h-20 rounded-huge" />
        </div>
      </Layout>
    );
  }

  // ── Inline SVG bar chart ─────────────────────────────────
  const renderMonthlyChart = () => {
    const data = monthlyChartData.map(d => ({
      mes: SHORT_MONTHS[parseInt(d.periodo.split('-')[1], 10) - 1],
      Ingresos: d.ingresos,
      Gastos: d.gastos,
    }));

    if (data.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-sm text-calm-muted font-light">
          <Icon name="bar_chart" className="text-[40px] text-slate-300 mb-2" />
          Sin datos de evolución para este periodo
        </div>
      );
    }

    const fmtAxis = (v: number) => {
      if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M€`;
      if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}k€`;
      return `${v}€`;
    };

    const ChartTooltip = ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;
      return (
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xl text-xs min-w-[140px]">
          <p className="font-semibold text-calm-text mb-2">{label}</p>
          {payload.map((entry: any, i: number) => (
            <div key={i} className="flex items-center gap-2 mb-1 last:mb-0">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
              <span className="text-calm-muted">{entry.name}:</span>
              <span className="font-medium text-calm-text ml-auto pl-2">{formatEur(entry.value)}</span>
            </div>
          ))}
        </div>
      );
    };

    return (
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={{ top: 12, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          <XAxis
            dataKey="mes"
            tick={{ fontSize: 11, fill: '#94a3b8', fontFamily: 'Inter, sans-serif' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={fmtAxis}
            tick={{ fontSize: 11, fill: '#94a3b8', fontFamily: 'Inter, sans-serif' }}
            axisLine={false}
            tickLine={false}
            width={56}
          />
          <Tooltip content={<ChartTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12, fontFamily: 'Inter, sans-serif', paddingTop: 12, color: '#64748b' }} />
          <Bar dataKey="Ingresos" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={32} fillOpacity={0.85} />
          <Bar dataKey="Gastos" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={32} fillOpacity={0.75} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <>
      <Head>
        <title>Panel Financiero — Externaliza2</title>
      </Head>
      <Layout rol="cliente">
        <div className="h-[calc(100vh-72px)] overflow-y-auto px-8 md:px-12 pb-16 space-y-10 pt-8 scrollbar-thin">

          {/* ─── 1. Header ──────────────────────────────────── */}
          <div className="flex flex-wrap justify-between items-end gap-4 animate-fade-in">
            <div>
              <h1 className="page-title">Mi Panel Financiero</h1>
              <p className="page-subtitle">
                {clienteInfo?.nombre_comercial || clienteInfo?.razon_social || 'Mi empresa'} · Resumen económico y fiscal
              </p>
            </div>
            {lastSyncAt && (
              <span className="badge badge-info flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-soft" />
                <Icon name="sync" className="text-[14px]" />
                Sincronizado {formatRelativeTime(lastSyncAt)}
              </span>
            )}
          </div>

          {/* ─── 2. Period Filter ───────────────────────────── */}
          <div className="flex gap-1 bg-slate-100/80 rounded-2xl p-1 w-fit animate-slide-up stagger-1">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={cn(
                  'px-5 py-2.5 rounded-xl text-sm font-light transition-all duration-200',
                  periodFilter === opt.value
                    ? 'bg-white text-fresh-sky-600 shadow-sm font-medium'
                    : 'text-calm-muted hover:text-calm-text'
                )}
                onClick={() => setPeriodFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* ─── Data loading skeleton ──────────────────────── */}
          {loadingData && (
            <div className="space-y-8 animate-fade-in">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-36 rounded-huge" />
                ))}
              </div>
              <Skeleton className="h-48 rounded-huge" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-32 rounded-huge" />
                ))}
              </div>
              <Skeleton className="h-20 rounded-huge" />
            </div>
          )}

          {/* ─── No data for period ────────────────────────── */}
          {!loadingData && datosFinancieros.length === 0 && taxSummaries.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 animate-fade-in">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-fresh-sky-50 to-steel-azure-50 flex items-center justify-center mb-6">
                <Icon name="search_off" className="text-[40px] text-fresh-sky-300" />
              </div>
              <p className="text-lg font-light text-calm-text font-display mb-2">
                Sin datos para este periodo
              </p>
              <p className="text-sm text-calm-muted font-light max-w-md text-center">
                No hay información financiera ni fiscal disponible en el periodo seleccionado.
                Los datos se generan automáticamente al contabilizar tus facturas.
              </p>
            </div>
          )}

          {/* ─── Dashboard content ──────────────────────────── */}
          {!loadingData && (datosFinancieros.length > 0 || taxSummaries.length > 0) && (
            <>
              {/* ─── 3. KPI Cards Row ──────────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Ingresos */}
                <div className="bg-gradient-to-br from-emerald-50/60 to-white border border-emerald-200/40 rounded-huge p-7 animate-slide-up stagger-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Icon name="trending_up" className="text-[18px] text-emerald-600" />
                    </div>
                    <span className="stat-label !mt-0">Ingresos</span>
                  </div>
                  <p className="text-2xl font-light text-emerald-700 font-display tabular-nums">
                    {formatEur(kpis.totalIngresos)}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted mt-2">
                    Periodo seleccionado
                  </p>
                </div>

                {/* Gastos */}
                <div className="bg-gradient-to-br from-red-50/50 to-white border border-red-200/40 rounded-huge p-7 animate-slide-up stagger-2">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
                      <Icon name="trending_down" className="text-[18px] text-red-500" />
                    </div>
                    <span className="stat-label !mt-0">Gastos</span>
                  </div>
                  <p className="text-2xl font-light text-red-600 font-display tabular-nums">
                    {formatEur(kpis.totalGastos)}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted mt-2">
                    Periodo seleccionado
                  </p>
                </div>

                {/* Resultado Neto */}
                <div className={cn(
                  'rounded-huge p-7 animate-slide-up stagger-3 border',
                  kpis.totalResultado >= 0
                    ? 'bg-gradient-to-br from-emerald-50/40 to-white border-emerald-200/40'
                    : 'bg-gradient-to-br from-red-50/40 to-white border-red-200/40'
                )}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={cn(
                      'w-9 h-9 rounded-xl flex items-center justify-center',
                      kpis.totalResultado >= 0 ? 'bg-emerald-100' : 'bg-red-100'
                    )}>
                      <Icon
                        name="account_balance"
                        className={cn('text-[18px]', kpis.totalResultado >= 0 ? 'text-emerald-600' : 'text-red-500')}
                      />
                    </div>
                    <span className="stat-label !mt-0">Resultado Neto</span>
                  </div>
                  <p className={cn(
                    'text-2xl font-light font-display tabular-nums',
                    kpis.totalResultado >= 0 ? 'text-emerald-700' : 'text-red-600'
                  )}>
                    {formatEur(kpis.totalResultado)}
                  </p>
                  <p className={cn(
                    'text-[10px] uppercase tracking-widest mt-2',
                    kpis.totalResultado >= 0 ? 'text-emerald-500' : 'text-red-400'
                  )}>
                    {kpis.totalResultado >= 0 ? 'Beneficio' : 'Pérdida'}
                  </p>
                </div>

                {/* Margen */}
                <div className={cn(
                  'rounded-huge p-7 animate-slide-up stagger-4 border',
                  kpis.margen >= 15
                    ? 'bg-gradient-to-br from-fresh-sky-50/50 to-white border-fresh-sky-200/40'
                    : kpis.margen >= 0
                    ? 'bg-gradient-to-br from-amber-50/40 to-white border-amber-200/40'
                    : 'bg-gradient-to-br from-red-50/40 to-white border-red-200/40'
                )}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={cn(
                      'w-9 h-9 rounded-xl flex items-center justify-center',
                      kpis.margen >= 15 ? 'bg-fresh-sky-100' : kpis.margen >= 0 ? 'bg-amber-100' : 'bg-red-100'
                    )}>
                      <Icon
                        name="speed"
                        className={cn(
                          'text-[18px]',
                          kpis.margen >= 15 ? 'text-fresh-sky-600' : kpis.margen >= 0 ? 'text-amber-600' : 'text-red-500'
                        )}
                      />
                    </div>
                    <span className="stat-label !mt-0">Margen</span>
                  </div>
                  <p className={cn(
                    'text-2xl font-light font-display tabular-nums',
                    kpis.margen >= 15 ? 'text-fresh-sky-700' : kpis.margen >= 0 ? 'text-amber-700' : 'text-red-600'
                  )}>
                    {kpis.margen.toFixed(1)}%
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted mt-2">
                    {kpis.margen >= 15 ? 'Saludable' : kpis.margen >= 0 ? 'Ajustado' : 'Negativo'}
                  </p>
                </div>
              </div>

              {/* ─── 4. Monthly Evolution Chart ────────────── */}
              <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 animate-slide-up stagger-3">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="section-title">Evolución Mensual</h2>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-calm-muted">
                      <span className="w-2.5 h-2.5 rounded-sm bg-emerald-600 opacity-75" />
                      Ingresos
                    </span>
                    <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-calm-muted">
                      <span className="w-2.5 h-2.5 rounded-sm bg-red-600 opacity-65" />
                      Gastos
                    </span>
                  </div>
                </div>
                {renderMonthlyChart()}
              </div>

              {/* ─── 5. Tax Summary Section ────────────────── */}
              {taxSummaries.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* IVA Repercutido */}
                  <div className="bg-gradient-to-br from-fresh-sky-50/60 to-white border border-fresh-sky-200/40 rounded-huge p-7 animate-slide-up stagger-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-xl bg-fresh-sky-100 flex items-center justify-center">
                        <Icon name="receipt_long" className="text-[18px] text-fresh-sky-600" />
                      </div>
                      <span className="stat-label !mt-0">IVA Repercutido</span>
                    </div>
                    <p className="text-2xl font-light text-fresh-sky-700 font-display tabular-nums">
                      {formatEur(taxTotals.ivaRepercutido)}
                    </p>
                  </div>

                  {/* IVA Soportado */}
                  <div className="bg-gradient-to-br from-slate-50/60 to-white border border-slate-200/40 rounded-huge p-7 animate-slide-up stagger-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
                        <Icon name="shopping_cart" className="text-[18px] text-slate-500" />
                      </div>
                      <span className="stat-label !mt-0">IVA Soportado</span>
                    </div>
                    <p className="text-2xl font-light text-slate-600 font-display tabular-nums">
                      {formatEur(taxTotals.ivaSoportado)}
                    </p>
                  </div>

                  {/* Resultado IVA */}
                  <div className={cn(
                    'rounded-huge p-7 animate-slide-up stagger-6 border',
                    taxTotals.ivaResultado > 0
                      ? 'bg-gradient-to-br from-fresh-sky-50/40 to-white border-fresh-sky-200/40'
                      : 'bg-gradient-to-br from-red-50/40 to-white border-red-200/40'
                  )}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={cn(
                        'w-9 h-9 rounded-xl flex items-center justify-center',
                        taxTotals.ivaResultado > 0 ? 'bg-fresh-sky-100' : 'bg-red-100'
                      )}>
                        <Icon
                          name="balance"
                          className={cn('text-[18px]', taxTotals.ivaResultado > 0 ? 'text-fresh-sky-600' : 'text-red-500')}
                        />
                      </div>
                      <span className="stat-label !mt-0">Resultado IVA</span>
                    </div>
                    <p className={cn(
                      'text-2xl font-light font-display tabular-nums',
                      taxTotals.ivaResultado > 0 ? 'text-fresh-sky-700' : 'text-red-600'
                    )}>
                      {formatEur(taxTotals.ivaResultado)}
                    </p>
                    <p className={cn(
                      'text-[10px] uppercase tracking-widest mt-2',
                      taxTotals.ivaResultado > 0 ? 'text-fresh-sky-500' : 'text-red-400'
                    )}>
                      {taxTotals.ivaResultado > 0 ? 'A ingresar' : 'A devolver'}
                    </p>
                  </div>
                </div>
              )}

              {/* ─── 5b. Retenciones y Base Imponible ─────── */}
              {taxSummaries.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                  {/* Retenciones Practicadas */}
                  <div className="bg-gradient-to-br from-fresh-sky-50/60 to-white border border-fresh-sky-200/40 rounded-huge p-7 animate-slide-up stagger-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-xl bg-fresh-sky-100 flex items-center justify-center">
                        <Icon name="savings" className="text-[18px] text-fresh-sky-600" />
                      </div>
                      <span className="stat-label !mt-0">Retenciones Practicadas</span>
                    </div>
                    <p className="text-2xl font-light text-fresh-sky-700 font-display tabular-nums">
                      {formatEur(taxTotals.retencionesPracticadas)}
                    </p>
                  </div>

                  {/* Retenciones Soportadas */}
                  <div className="bg-gradient-to-br from-slate-50/60 to-white border border-slate-200/40 rounded-huge p-7 animate-slide-up stagger-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
                        <Icon name="savings" className="text-[18px] text-slate-500" />
                      </div>
                      <span className="stat-label !mt-0">Retenciones Soportadas</span>
                    </div>
                    <p className="text-2xl font-light text-slate-600 font-display tabular-nums">
                      {formatEur(taxTotals.retencionesSoportadas)}
                    </p>
                  </div>

                  {/* Base Imponible Emitidas */}
                  <div className="bg-gradient-to-br from-emerald-50/60 to-white border border-emerald-200/40 rounded-huge p-7 animate-slide-up stagger-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <Icon name="receipt" className="text-[18px] text-emerald-600" />
                      </div>
                      <span className="stat-label !mt-0">Base Imponible Emitidas</span>
                    </div>
                    <p className="text-2xl font-light text-emerald-700 font-display tabular-nums">
                      {formatEur(taxTotals.baseImponibleEmitidas)}
                    </p>
                  </div>

                  {/* Base Imponible Recibidas */}
                  <div className="bg-gradient-to-br from-slate-50/60 to-white border border-slate-200/40 rounded-huge p-7 animate-slide-up stagger-7">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
                        <Icon name="receipt" className="text-[18px] text-slate-500" />
                      </div>
                      <span className="stat-label !mt-0">Base Imponible Recibidas</span>
                    </div>
                    <p className="text-2xl font-light text-slate-600 font-display tabular-nums">
                      {formatEur(taxTotals.baseImponibleRecibidas)}
                    </p>
                  </div>
                </div>
              )}

              {/* ─── 5c. Suplidos ───────────────────────────── */}
              {taxSummaries.length > 0 && taxTotals.totalSuplidos > 0 && (
                <div className="bg-gradient-to-br from-slate-50/60 to-white border border-slate-200/40 rounded-huge p-7 animate-slide-up stagger-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Icon name="account_balance_wallet" className="text-[18px] text-slate-500" />
                    </div>
                    <span className="stat-label !mt-0">Total Suplidos</span>
                  </div>
                  <p className="text-2xl font-light text-slate-700 font-display tabular-nums">
                    {formatEur(taxTotals.totalSuplidos)}
                  </p>
                  <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-calm-muted mt-2">
                    <Icon name="info" className="text-[12px]" />
                    Gastos suplidos (no sujetos a IVA)
                  </p>
                </div>
              )}

              {/* ─── 6. Documents Status Row ───────────────── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-slide-up stagger-5">
                {/* Pendientes */}
                <div className="flex items-center gap-3 bg-gradient-to-br from-amber-50/50 to-white border border-amber-200/30 rounded-2xl px-5 py-4">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Icon name="pending" className="text-[16px] text-amber-600" />
                  </div>
                  <div>
                    <p className="text-lg font-light text-amber-700 font-display tabular-nums">{docCounts.pendientes}</p>
                    <p className="text-[9px] uppercase tracking-widest text-calm-muted">Pendientes</p>
                  </div>
                </div>

                {/* Contabilizados */}
                <div className="flex items-center gap-3 bg-gradient-to-br from-emerald-50/50 to-white border border-emerald-200/30 rounded-2xl px-5 py-4">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Icon name="check_circle" className="text-[16px] text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-lg font-light text-emerald-700 font-display tabular-nums">{docCounts.contabilizados}</p>
                    <p className="text-[9px] uppercase tracking-widest text-calm-muted">Contabilizados</p>
                  </div>
                </div>

                {/* Incidencias */}
                <div className="flex items-center gap-3 bg-gradient-to-br from-red-50/50 to-white border border-red-200/30 rounded-2xl px-5 py-4">
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                    <Icon name="error" className="text-[16px] text-red-500" />
                  </div>
                  <div>
                    <p className="text-lg font-light text-red-600 font-display tabular-nums">{docCounts.incidencias}</p>
                    <p className="text-[9px] uppercase tracking-widest text-calm-muted">Incidencias</p>
                  </div>
                </div>

                {/* Total docs */}
                <div className="flex items-center gap-3 bg-gradient-to-br from-steel-azure-50/50 to-white border border-steel-azure-200/30 rounded-2xl px-5 py-4">
                  <div className="w-8 h-8 rounded-lg bg-steel-azure-100 flex items-center justify-center">
                    <Icon name="folder" className="text-[16px] text-steel-azure-600" />
                  </div>
                  <div>
                    <p className="text-lg font-light text-steel-azure-700 font-display tabular-nums">{docCounts.total}</p>
                    <p className="text-[9px] uppercase tracking-widest text-calm-muted">Total docs</p>
                  </div>
                </div>
              </div>

              {/* ─── 7. Invoicing Summary Card ─────────────── */}
              {latestInvoicing && (
                <div className="bg-gradient-to-br from-white to-slate-50/30 border border-calm-border rounded-huge p-7 animate-slide-up stagger-6">
                  <div className="flex items-center gap-3 mb-5">
                    <Icon name="description" className="text-[20px] text-calm-muted" />
                    <h2 className="section-title">Resumen de Facturación</h2>
                    <span className="text-sm text-calm-muted font-light ml-1">
                      Periodo {latestInvoicing.periodo}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-calm-border/50">
                      <div className="w-10 h-10 rounded-xl bg-fresh-sky-50 flex items-center justify-center">
                        <Icon name="arrow_upward" className="text-[20px] text-fresh-sky-600" />
                      </div>
                      <div>
                        <p className="stat-label !mt-0">Facturas Emitidas</p>
                        <p className="text-2xl font-light text-fresh-sky-700 font-display tabular-nums mt-1">
                          {num(latestInvoicing.num_facturas_emitidas)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-calm-border/50">
                      <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center">
                        <Icon name="arrow_downward" className="text-[20px] text-slate-500" />
                      </div>
                      <div>
                        <p className="stat-label !mt-0">Facturas Recibidas</p>
                        <p className="text-2xl font-light text-slate-600 font-display tabular-nums mt-1">
                          {num(latestInvoicing.num_facturas_recibidas)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ─── Footer sync note ──────────────────────── */}
              {lastSyncAt && (
                <div className="flex items-center justify-end gap-2 pt-2 pb-4 animate-fade-in">
                  <Icon name="update" className="text-[14px] text-calm-accent" />
                  <p className="text-[11px] text-calm-accent font-light">
                    Última sincronización: {new Date(lastSyncAt).toLocaleString('es-ES', {
                      day: 'numeric', month: 'long', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Layout>
    </>
  );
}
